// frontend/src/components/ScheduleOverview.jsx

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferenceContext';
import { getGlobalThreshold } from '../utils/preferences';
import { AlarmClockPlus} from 'lucide-react';
import 'tippy.js/dist/tippy.css'; // Import the CSS for styling
import moment from 'moment-timezone'; // Use moment-timezone for time zone handling
import ScheduleList from './ScheduleList'; // Import the ScheduleList component
import PrioritiseModal from './PrioritiseModal'; // Import the PrioritiseModal component
import { formatDate } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';


function getEffectiveTimeSlots(schedule) {
  const effectiveTimeSlots = [];

  // Parse included_days as numerical IDs
  const includedDays = schedule.included_days
    ? schedule.included_days.split(',').map(dayStr => parseInt(dayStr.trim(), 10)).filter(id => id >= 0 && id <= 6)
    : [0, 1, 2, 3, 4, 5, 6]; // Default to all days if not specified

  // console.log(`Processing Schedule: ${schedule.name}`);
  // console.log(`Included Days (IDs): ${includedDays}`);

  if (schedule.type === 'on_call') {
    // For 'on_call' schedules, use on_call_start_time and on_call_end_time
    if (schedule.on_call_start_time && schedule.on_call_end_time) {
      includedDays.forEach((day) => {
        const slotInfo = {
          day, // Day of the week (0-6)
          startTime: moment(schedule.on_call_start_time, 'HH:mm:ss').format('HH:mm'),
          endTime: moment(schedule.on_call_end_time, 'HH:mm:ss').format('HH:mm'),
        };
        effectiveTimeSlots.push(slotInfo);
      });
      // console.log(`On-Call Time Slots:`, effectiveTimeSlots);
    }
  } else {
    // For non 'on_call' schedules, process time_slots
    if (Array.isArray(schedule.time_slots)) {
      schedule.time_slots.forEach((slot) => {
        includedDays.forEach((day) => {
          let startTime = moment(slot.start_time, 'HH:mm:ss').format('HH:mm');
          let endTime;

          if (slot.type === 'recurring' && slot.end_time) {
            endTime = moment(slot.end_time, 'HH:mm:ss').format('HH:mm');
          } else {
            // For 'single' time slots, set endTime to startTime or add a minimal duration
            endTime = startTime; // Alternatively, you can add a minimal duration here
          }

          const slotInfo = {
            day, // Day of the week (0-6)
            startTime,
            endTime,
          };
          effectiveTimeSlots.push(slotInfo);
        });
      });
    }
  }

  return effectiveTimeSlots;
}

function schedulesOverlap(scheduleA, scheduleB) {
  // Check date range overlap
  const startA = moment(scheduleA.start_date);
  const endA = moment(scheduleA.end_date);
  const startB = moment(scheduleB.start_date);
  const endB = moment(scheduleB.end_date);
  
  if (endA.isBefore(startB, 'day') || endB.isBefore(startA, 'day')) {
    return false; // No date overlap
  }

  // Find overlapping days using numerical IDs
  const overlappingDays = scheduleA.effectiveTimeSlots.map(slot => slot.day)
    .filter(day => scheduleB.effectiveTimeSlots.some(bSlot => bSlot.day === day));

  if (overlappingDays.length === 0) {
    return false; // No overlapping days
  }

  // Check time slot overlaps for overlapping days
  for (const day of overlappingDays) {
    const slotsA = scheduleA.effectiveTimeSlots.filter(slot => slot.day === day);
    const slotsB = scheduleB.effectiveTimeSlots.filter(slot => slot.day === day);
    
    for (const slotA of slotsA) {
      for (const slotB of slotsB) {
        // Convert times to moments for comparison
        const startA = moment(slotA.startTime, 'HH:mm');
        const endA = moment(slotA.endTime, 'HH:mm');
        const startB = moment(slotB.startTime, 'HH:mm');
        const endB = moment(slotB.endTime, 'HH:mm');

        // Adjust for cases where endTime may be before startTime (e.g., overnight shifts)
        if (endA.isBefore(startA)) endA.add(1, 'day');
        if (endB.isBefore(startB)) endB.add(1, 'day');

        // Handle Single Trip cases where start_time == end_time
        const isSingleTripA = startA.isSame(endA);
        const isSingleTripB = startB.isSame(endB);

        // Check for overlap
        if (isSingleTripA && isSingleTripB) {
        // Both are Single Trips
          return startA.isSame(startB);
        } else if (isSingleTripA) {
          // Slot A is a Single Trip
          return startA.isBetween(startB, endB, null, '[]') || startA.isSame(startB) || startA.isSame(endB);
        } else if (isSingleTripB) {
          // Slot B is a Single Trip
          return startB.isBetween(startA, endA, null, '[]') || startB.isSame(startA) || startB.isSame(endA);
        } else {
          // Both are time ranges
          return endA.isAfter(startB) && startA.isBefore(endB);
        }
      }
    }
  }

  return false; // No overlapping time slots
}

function detectScheduleConflicts(schedules) {
  const conflicts = {};
  const scheduleMap = {};

  // Preprocess schedules to get effective active periods
  const processedSchedules = schedules.map(schedule => ({
    ...schedule,
    effectiveTimeSlots: getEffectiveTimeSlots(schedule),
  }));

  // Create scheduleMap for easy lookup
  processedSchedules.forEach(schedule => {
    scheduleMap[schedule.id] = schedule;
  });

  // console.log('Processed Schedules:', processedSchedules);

  // Compare each pair of schedules to detect overlaps
  for (let i = 0; i < processedSchedules.length; i++) {
    const scheduleA = processedSchedules[i];
    for (let j = i + 1; j < processedSchedules.length; j++) {
      const scheduleB = processedSchedules[j];
      
      // console.log(`Checking overlap between: ${scheduleA.name} and ${scheduleB.name}`);
      const overlap = schedulesOverlap(scheduleA, scheduleB);
      // console.log(`Overlap Detected: ${overlap}`);

      if (overlap) {
        // Initialize conflict arrays if not already
        conflicts[scheduleA.id] = conflicts[scheduleA.id] || [];
        conflicts[scheduleB.id] = conflicts[scheduleB.id] || [];
        
        // Add each schedule as conflicting with the other
        conflicts[scheduleA.id].push(scheduleB.id);
        conflicts[scheduleB.id].push(scheduleA.id);
      }
    }
  }

  // console.log('Conflicts Detected:', conflicts);
  // console.log('Schedule Map:', scheduleMap);

  return { conflicts, scheduleMap };
}

// capitalize the first letter of a string
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const ScheduleOverview = ({ 
  selectedRoute, 
  availableSchedules, 
  appliedSchedules, 
  onCreateSchedule, 
  openEditModal,
  openCopyModal,
  onApplySchedule,
  onUnApplySchedule,
  onUpdatePriority,
  onSelectSchedule,
  onDeleteSchedule, 
  currentEntity, 
  isLoading, 

}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  const { entitySettings } = usePreferences();
  // Force re-render when preferences change
  useDateTimePreferences();
  const [showExpired, setShowExpired] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null); // Initialize as null
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [availableSearchTerm, setAvailableSearchTerm] = useState('');
  // const itemHeight = 40; // Define item height based on the UI

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScheduleForPrioritise, setSelectedScheduleForPrioritise] = useState(null);

  const globalThreshold = getGlobalThreshold();
  const userId = parseInt(localStorage.getItem('userId'), 10);
  // console.log('userId:', userId);
  const entity_type = capitalize(localStorage.getItem('entity_type')) || "N/A"; // Default to 'N/A' if not found;
  // Handle selection of schedules
  const handleSelectSchedule = (scheduleId) => {
    setSelectedScheduleId(prevSelected => (prevSelected === scheduleId ? null : scheduleId));
  };

  // Function to apply date filters only
  const filterSchedulesByDate = (scheduleList) => {
    return scheduleList.filter(schedule => {
      // Get timezone from entity settings (EAV preference system)
      const entityTimeZone = entitySettings?.time_zone || currentEntity.time_zone || 'UTC';

      // Parse the schedule's end date in the entity's time zone
      const scheduleEndDate = moment.tz(schedule.end_date, ['YYYY-MM-DD', moment.ISO_8601], entityTimeZone);

      // Check if the date is valid
      if (!scheduleEndDate.isValid()) {
        console.error('Invalid end date for schedule:', schedule);
        return false; // Exclude schedules with invalid end dates
      }

      // Get the current date in the entity's time zone
      const currentDate = moment.tz(entityTimeZone);

      // Determine if the schedule is expired
      const isExpired = scheduleEndDate.isBefore(currentDate, 'day');

      // Exclude expired schedules if "Show Expired" is unchecked
      if (isExpired && !showExpired) {
        return false;
      }

      return true;
    });
  };

  // Function to apply search term filters
  const filterSchedulesBySearchTerm = (scheduleList, searchTerm) => {
    if (searchTerm.trim() === '') {
      return scheduleList;
    }
    return scheduleList.filter(schedule => {
      const scheduleName = schedule.name.toLowerCase();
      return scheduleName.includes(searchTerm.toLowerCase());
    });
  };


  const isScheduleForCurrentEntity = (schedule) => {
    if (schedule.entity_id === null) {
      return false;
    }
    return parseInt(schedule.entity_id, 10) === parseInt(currentEntity.entityId, 10);
  };

  const sortSchedules = (scheduleList) => {
    const entitySpecificSchedules = scheduleList.filter(schedule => isScheduleForCurrentEntity(schedule));
    const commonSchedules = scheduleList.filter(schedule => schedule.entity_id === null);
    const otherEntitySchedules = scheduleList.filter(schedule => 
      schedule.entity_id !== null && parseInt(schedule.entity_id, 10) !== parseInt(currentEntity.entityId, 10)
    );
    return [...entitySpecificSchedules, ...commonSchedules, ...otherEntitySchedules];
  };

  // Applied Schedules
  const appliedSchedulesByDate = filterSchedulesByDate(appliedSchedules);
  const filteredAppliedSchedules = filterSchedulesBySearchTerm(appliedSchedulesByDate, appliedSearchTerm);
  const sortedAppliedSchedules = sortSchedules(filteredAppliedSchedules);

  // Preprocessing step
  const processedAppliedSchedules = sortedAppliedSchedules.map(schedule => ({
    ...schedule,
    effectiveTimeSlots: getEffectiveTimeSlots(schedule),
  }));

  // Detect conflicts and get scheduleMap
  const { conflicts, scheduleMap } = detectScheduleConflicts(processedAppliedSchedules);


  // Available Schedules
  const availableSchedulesByDate = filterSchedulesByDate(availableSchedules);
  const filteredAvailableSchedules = filterSchedulesBySearchTerm(availableSchedulesByDate, availableSearchTerm);
  const sortedAvailableSchedules = sortSchedules(filteredAvailableSchedules);

  const createTippyTooltip = (schedule) => (
    <div>
      <p><strong>{schedule.created_by === userId ? 'M' : 'O'}</strong> : <strong>M:</strong> Created by Me;  <strong>O:</strong> by Other User</p>
      <p><strong>F:</strong> Fixed, <strong>OC:</strong> On-Call</p>
      <p><strong>Date:</strong> From {formatDate(schedule.start_date)} to {formatDate(schedule.end_date)}</p>
      <p><strong>Days:</strong> {formatIncludedDays(schedule.included_days)}</p>
      <p><strong>S:</strong> Single trip, <strong>R:</strong> Recurring trip</p>
      {schedule.excluded_periods.length > 0 && (
        <p><strong>Excl P:</strong> Excluded periods</p>
      )}
      {schedule.priority != null && schedule.priority !== "" && (
        <p>
          <strong>Priority:</strong> {`${schedule.priority} : 0-Highest,1-Second Highest, ...`}
        </p>
      )}
    </div>
  );
  
  // Helper function to format included days
  const formatIncludedDays = (includedDays) => {
    if (!includedDays) return 'N/A';
    
    // Assuming 'included_days' is a comma-separated string like "0,1,2,3,4,5,6"
    const daysMap = {
      0: 'Sun',
      1: 'Mon',
      2: 'Tue',
      3: 'Wed',
      4: 'Thu',
      5: 'Fri',
      6: 'Sat'
    };
    
    const daysArray = includedDays.split(',').map(day => day.trim()).filter(day => day !== '');
    
    return daysArray.map(day => `${day}-${daysMap[day]}`).join('; ');
  };
  
  // Helper functions
  /**
   * Generates a snapshot string for a given schedule.
   * @param {Object} schedule - The schedule object.
   * @returns {string} - Formatted snapshot string.
   */
  const getScheduleSnapshot = (schedule) => {
    
    // Snapshot Format: {Created_by, Type, Date Range, Included Days, Trip Type, Excluded Periods, Priority}
    // 0. determine who created the schedule
    const createdBy = schedule.created_by === userId ? 'M' : 'O'; // M for Me, O for Others
  
    // 1. Determine Schedule Type
    const type = schedule.type === 'on_call' ? 'OC' : 'F'; // OC for On-Call, F for Fixed
  
    // 2. Format Date Range (compact format for grid display)
    const startDate = formatDate(schedule.start_date);
    const endDate = formatDate(schedule.end_date);
    const dateRange = `${startDate}→${endDate}`;
  
    // 3. Get Included Days ID
    // Assuming 'included_days' is a comma-separated string like "1,2,3,4,5"
    const includedDays = schedule.included_days
      ? schedule.included_days.split(',').map(day => parseInt(day.trim(), 10)).filter(day => !isNaN(day))
      : [];
  
    let includedDaysId = 'N/A';
    if (includedDays.length > 0) {
      // Convert array of numbers into a compact string (e.g., [1,2,3,4,5] => "1-5" or [0,1,2,3,4,6] => "0-4,6")
      const sortedDays = [...new Set(includedDays)].sort((a, b) => a - b);
      const ranges = [];
      let start = sortedDays[0];
      let end = sortedDays[0];
  
      for (let i = 1; i < sortedDays.length; i++) {
        if (sortedDays[i] === end + 1) {
          end = sortedDays[i];
        } else {
          if (start === end) {
            ranges.push(`${start}`);
          } else if (start + 1 === end) {
            ranges.push(`${start}${end}`);
          } else {
            ranges.push(`${start}→${end}`);
          }
          start = sortedDays[i];
          end = sortedDays[i];
        }
      }
  
      // Add the last range
      if (start === end) {
        ranges.push(`${start}`);
      } else if (start + 1 === end) {
        ranges.push(`${start}${end}`);
      } else {
        ranges.push(`${start}→${end}`);
      }
  
      includedDaysId = ranges.join('');
    }
  
    // 4. Determine Trip Type (Single, Recurring, Both)
    const tripTypes = new Set();
    if (Array.isArray(schedule.time_slots) && schedule.time_slots.length > 0) {
      schedule.time_slots.forEach(slot => {
        if (slot.type === 'single') {
          tripTypes.add('s');
        } else if (slot.type === 'recurring') {
          tripTypes.add('r');
        }
      });
    }
    const tripType = tripTypes.size === 2 ? 's/r' : tripTypes.size === 1 ? [...tripTypes][0] : '';
  
    // 5. Check for Excluded Periods
    const hasExcludedPeriods = schedule.excluded_periods && schedule.excluded_periods.length > 0;
    const exclP = hasExcludedPeriods ? 'excl p' : '';
  
    // 6. Combine All Components
    const snapshotComponents = [createdBy, type, dateRange, includedDaysId, tripType, exclP, schedule.priority]
      .filter(component => component !== undefined && component !== null && component !== '') // Keep 0 and other falsy values except undefined, null, and empty strings
      .join(', ');
  
    return `{${snapshotComponents}}`;
  };

  return (
    
    <div className={cn("w-full lg:w-2/3 p-6 flex flex-col flex-grow", background.primary)}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className={cn("text-2xl font-bold", text.primary)}>{t('Schedules')}</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <button 
            onClick={onCreateSchedule}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
              "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            )}
          >
            <AlarmClockPlus size={18} />
            {t('New Schedule')}
          </button>
          <label className={cn("flex items-center gap-2", text.secondary)}>
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">{t('Show Expired')}</span>
          </label>
        </div>
      </div>

      {/* Applied Schedules Section */}
      {selectedRoute && (
       <div className="flex flex-col mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700" style={{ maxHeight: '45%' }}>
          <div className={cn(
            "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4",
            border.default,
            "border-b"
          )}>
            <h3 className={cn("text-lg font-semibold", text.primary)}>
              {t('Applied Schedules')} ({appliedSchedulesByDate.length})
            </h3>
            {appliedSchedulesByDate.length >= globalThreshold && (
              <input
                type="text"
                className={cn(
                  "px-3 py-2 rounded-lg text-sm transition-colors",
                  "border-2 border-gray-300 dark:border-gray-600",
                  "focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800",
                  "bg-white dark:bg-gray-700",
                  text.primary,
                  "placeholder-gray-500 dark:placeholder-gray-400"
                )}
                placeholder={t('Search Schedules')}
                value={appliedSearchTerm}
                onChange={(e) => setAppliedSearchTerm(e.target.value)}
              />
            )}
          </div>

          <div className="overflow-y-auto flex-grow p-4">  
            {sortedAppliedSchedules.length > 0 ? (
              <>
                {/* Entity-Specific Applied Schedules */}
                {sortedAppliedSchedules.filter(isScheduleForCurrentEntity).length > 0 && (
                  <>
                    <h4 className={cn("text-md font-semibold mb-3", text.secondary)}>{t(`${entity_type}-Specific Schedules`)}</h4>
                    <ScheduleList
                      schedules={sortedAppliedSchedules.filter(isScheduleForCurrentEntity)}
                      conflicts={conflicts}
                      scheduleMap={scheduleMap}
                      currentEntity={currentEntity}
                      selectedScheduleId={selectedScheduleId}
                      handleSelectSchedule={handleSelectSchedule}
                      onSelectSchedule={onSelectSchedule}
                      setSelectedScheduleForPrioritise={setSelectedScheduleForPrioritise}
                      setIsModalOpen={setIsModalOpen}
                      getScheduleSnapshot={getScheduleSnapshot}
                      createTippyTooltip={createTippyTooltip}
                      t={t}
                    />
                  </>
                )}

                {/* Common Applied Schedules */}
                {sortedAppliedSchedules.filter(schedule => schedule.entity_id === null).length > 0 && (
                  <>
                    <h4 className={cn("text-md font-semibold mt-4 mb-3", text.secondary)}>{t('Common Schedules')}</h4>
                    <ScheduleList
                      schedules={sortedAppliedSchedules.filter((schedule) => schedule.entity_id === null)}
                      conflicts={conflicts}
                      scheduleMap={scheduleMap}
                      currentEntity={currentEntity}
                      selectedScheduleId={selectedScheduleId}
                      handleSelectSchedule={handleSelectSchedule}
                      onSelectSchedule={onSelectSchedule}
                      setSelectedScheduleForPrioritise={setSelectedScheduleForPrioritise}
                      setIsModalOpen={setIsModalOpen}
                      getScheduleSnapshot={getScheduleSnapshot}
                      createTippyTooltip={createTippyTooltip}
                      t={t}
                    />
                  </>
                )}
              </>
            ) : (
              <p className={cn("text-center py-8", text.muted)}>{t('No applied schedules found.')}</p>              
            )}
          </div>
          {/* Prioritise Modal */}
          {isModalOpen && selectedScheduleForPrioritise && (
            <PrioritiseModal
              selectedSchedule={selectedScheduleForPrioritise}
              conflicts={conflicts[selectedScheduleForPrioritise.id]}
              scheduleMap={scheduleMap}
              onClose={() => setIsModalOpen(false)}
              onUpdatePriority={onUpdatePriority}
              getScheduleSnapshot={getScheduleSnapshot}
            />
          )}
        </div>
      )}

      {/* Available Schedules Section */}
      <div className="flex flex-col flex-grow overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className={cn(
          "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4",
          border.default,
          "border-b"
        )}>
          <h3 className={cn("text-lg font-semibold", text.primary)}>
            {t('Available Schedules')} ({availableSchedulesByDate.length})
          </h3>
          {availableSchedulesByDate.length >= globalThreshold && (
            <input
              type="text"
              className={cn(
                "px-3 py-2 rounded-lg text-sm transition-colors",
                "border-2 border-gray-300 dark:border-gray-600",
                "focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800",
                "bg-white dark:bg-gray-700",
                text.primary,
                "placeholder-gray-500 dark:placeholder-gray-400"
              )}
              placeholder={t('Search Schedules')}
              value={availableSearchTerm}
              onChange={(e) => setAvailableSearchTerm(e.target.value)}
            />
          )}
        </div>

        <div className="overflow-y-auto flex-grow p-4">
          {sortedAvailableSchedules.length > 0 ? (
            <>
              {/* Entity-Specific Available Schedules */}
              {sortedAvailableSchedules.filter(isScheduleForCurrentEntity).length > 0 && (
                <>
                  <h4 className={cn("text-md font-semibold mb-3", text.secondary)}>{t(`${entity_type}-Specific Schedules`)}</h4>
                  <ScheduleList
                    schedules={sortedAvailableSchedules.filter(isScheduleForCurrentEntity)}
                    conflicts={{}} // Assuming conflicts are not needed for available schedules
                    scheduleMap={{}} // Provide empty objects if not needed
                    currentEntity={currentEntity}
                    selectedScheduleId={selectedScheduleId}
                    handleSelectSchedule={handleSelectSchedule}
                    onSelectSchedule={onSelectSchedule}
                    setSelectedScheduleForPrioritise={setSelectedScheduleForPrioritise}
                    setIsModalOpen={setIsModalOpen}
                    getScheduleSnapshot={getScheduleSnapshot}
                    createTippyTooltip={createTippyTooltip}
                    t={t}
                  />
                </>
              )}

              {/* Common Available Schedules */}
              {sortedAvailableSchedules.filter(schedule => schedule.entity_id === null).length > 0 && (
                <>
                  <h4 className={cn("text-md font-semibold mt-4 mb-3", text.secondary)}>{t('Common Schedules')}</h4>
                  <ScheduleList
                    schedules={sortedAvailableSchedules.filter((schedule) => schedule.entity_id === null)}
                    conflicts={{}} // Assuming conflicts are not needed for available schedules
                    scheduleMap={{}} // Provide empty objects if not needed
                    currentEntity={currentEntity}
                    selectedScheduleId={selectedScheduleId}
                    handleSelectSchedule={handleSelectSchedule}
                    onSelectSchedule={onSelectSchedule}
                    setSelectedScheduleForPrioritise={setSelectedScheduleForPrioritise}
                    setIsModalOpen={setIsModalOpen}
                    getScheduleSnapshot={getScheduleSnapshot}
                    createTippyTooltip={createTippyTooltip}
                    t={t}
                  />
                </>
              )}
            </>
          ) : (
            <p className={cn("text-center py-8", text.muted)}>{t('No available schedules found.')}</p>
          )}
        </div>
      </div>

    </div>      
  );
};

export default ScheduleOverview;
